import { ref, watch } from "vue";
import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { useNgSession } from "@sazka/ng/ng-session";
import { logException } from "@sazka/utils/logger/use-app-logger";
import bonusomatPlayerStatus from "./data/bonusomat-player-status";
import fsPlayerStatus from "./data/fs-player-status";
import gwPlayerStatus from "./data/gw-player-status";
import { fetchLoyaltyPlayerStatus } from "../api/loyalty-api";
import { LoyaltyPlayerStatus } from "../api/models/loyalty-api-models";

const { isLoggedIn } = useNgSession();

type LoyaltyPlayerBasicData = Omit<LoyaltyPlayerStatus, "bonusomat" | "goldenWheel" | "furrySpin">;

const loyaltyPlayerStatus = ref<LoyaltyPlayerBasicData>({
    floatingSticker: null,
    vertical: null,
});

const initLoyaltyPlayerData = async () => {
    await ngFrameworkLoaded();

    watch(
        isLoggedIn,
        async (newIsLoggedIn) => {
            if (newIsLoggedIn) {
                try {
                    const newLoyaltyPlayerData = await fetchLoyaltyPlayerStatus();

                    loyaltyPlayerStatus.value.floatingSticker = newLoyaltyPlayerData.floatingSticker;
                    loyaltyPlayerStatus.value.vertical = newLoyaltyPlayerData.vertical;

                    bonusomatPlayerStatus.updateBonusomatData(
                        newLoyaltyPlayerData.bonusomat.points,
                        !newLoyaltyPlayerData.bonusomat.access
                    );
                    gwPlayerStatus.updateGwData(
                        newLoyaltyPlayerData.goldenWheel.points,
                        !newLoyaltyPlayerData.goldenWheel.access,
                        newLoyaltyPlayerData.goldenWheel.hasBonusWheel,
                        newLoyaltyPlayerData.goldenWheel.receivedCampaignPoints
                    );
                    fsPlayerStatus.updateFsData(
                        newLoyaltyPlayerData.furrySpin.points,
                        newLoyaltyPlayerData.furrySpin.access
                    );
                } catch (ex: unknown) {
                    logException("initLoyaltyPlayerData", ex);
                }
            }
        },
        { immediate: true }
    );
};

async function initBonusomatPlayerData() {
    await ngFrameworkLoaded();

    watch(
        isLoggedIn,
        async (newIsLoggedIn) => {
            if (newIsLoggedIn) {
                await bonusomatPlayerStatus.loadBonusomatPlayerData();
            } else {
                bonusomatPlayerStatus.clearPlayerData();
            }
        },
        { immediate: true }
    );
}

type LoyaltyPlayerDataInitType = "ALL" | "Bonusomat" | "GoldenWheel" | "FurrySpin" | "";

let isInitAllEnabled = true;
let isInitGwEnabled = true;
let isInitFsEnabled = true;
let isInitBonusomatEnabled = true;

// Dont use piniastore because websocket registration...
export function useLoyaltyPlayerStatus(initType?: LoyaltyPlayerDataInitType) {
    switch (initType) {
        case "ALL":
            if (isInitAllEnabled) {
                isInitAllEnabled = false;
                initLoyaltyPlayerData();
            }
            break;
        case "GoldenWheel":
            if (isInitGwEnabled) {
                isInitGwEnabled = false;
                gwPlayerStatus.initGwPlayerStatus();
            }
            break;
        case "FurrySpin":
            if (isInitFsEnabled) {
                isInitFsEnabled = false;
                fsPlayerStatus.initFsPlayerStatus();
            }
            break;
        case "Bonusomat":
            if (isInitBonusomatEnabled) {
                isInitBonusomatEnabled = false;
                initBonusomatPlayerData();
            }
            break;
    }

    return {
        loyaltyPlayerStatus,
        bonusomatPlayerStatus,
        gwPlayerStatus,
        fsPlayerStatus,
    };
}
